/**
 * G589 — Customer onboarding multi-step slideout.
 *
 * Replaces the 2-field "Onboard customer" modal that only created a
 * Customer row. This slideout drives the rich G587 endpoint, which
 * provisions Customer + first Org + first Tenant + 1-8 Instances +
 * first Customer Admin + grant + credential payload, all in one
 * atomic transaction.
 *
 * Steps:
 *   1. Customer        — canonical name, display name, optional billing email
 *   2. Topology        — org name, tenant name, 1-4 instances (add/remove)
 *   3. Admin           — email, display name, role, credential delivery mode
 *   4. Review          — read-only summary; the only step that submits
 *   5. Result          — display-once credentials (reset link OR temp password)
 *
 * Failure path: server returns 4xx/5xx → step stays on Review with an
 * inline error banner; user can fix the offending field and resubmit.
 * Server-side @Transactional rollback guarantees no half-created
 * customer ever lands in the DB.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Building2, Boxes, KeyRound, UserPlus, Plus, Trash2, Check, X,
  Copy, ExternalLink, Mail, ShieldAlert, AlertTriangle, ArrowLeft, ArrowRight,
  Server, Upload, Download, FileSpreadsheet,
} from 'lucide-react';
import { Slideout } from './Slideout';
import { Button } from './Button';
import { Pill } from './Pill';
import { Card } from './Card';
import * as api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

const STEP_CUSTOMER = 0;
const STEP_TOPOLOGY = 1;
const STEP_ADMIN    = 2;
const STEP_REVIEW   = 3;
const STEP_RESULT   = 4;

const STEP_LABELS = ['Customer', 'Topology', 'Admin', 'Review'];

const TIER_OPTIONS = [
  { value: 'DEV',        label: 'Development', isProduction: false },
  { value: 'STAGING',    label: 'Staging',     isProduction: false },
  { value: 'PRODUCTION', label: 'Production',  isProduction: true  },
  { value: 'CUSTOM',     label: 'Custom',      isProduction: false },
];

const DEFAULT_INSTANCE = { name: 'Production', tier: 'PRODUCTION', isProduction: true, description: '' };

/* RC-78 — Customer-onboarding admin role taxonomy.
 *
 * The first admin of a brand-new customer needs authority over the WHOLE
 * customer (all orgs that will be created inside it, all tenants under
 * those orgs, cross-org connector grants, RBAC role administration, etc.).
 * Per the RBAC wildcard model (Super Admin = customer-scoped "*"; Org Admin
 * = single-org "*"; see ConnectorShareScopeValidator + the BE
 * `canBypassScopeForCustomer` predicate), only Super Admin has that scope.
 *
 * Pre-RC-78 the slideout offered { Org Admin (default, mislabeled "full
 * customer-scope"), Tenant Admin }. That's wrong twice: (a) Org Admin
 * cannot manage other orgs in the same customer — a single Org Admin
 * cannot onboard a second org or grant connectors between orgs — and
 * (b) Tenant Admin is even narrower (single tenant, no role admin).
 *
 * Post-RC-78 the options are { Super Admin (default + recommended), Org
 * Admin, Tenant Admin }. Customers that intentionally want a narrower
 * first admin can still pick one of the lower tiers — but it's no longer
 * the silent default. */
const ADMIN_ROLE_OPTIONS = [
  { value: 'Super Admin',   label: 'Super Admin',   hint: 'Full admin across this entire customer — every org, every tenant, every instance (recommended)' },
  { value: 'Org Admin',     label: 'Org Admin',     hint: 'Admin scoped to the new org only — cannot manage other orgs added later' },
  { value: 'Tenant Admin',  label: 'Tenant Admin',  hint: 'Admin scoped to the new tenant only' },
];

const CANONICAL_NAME_RX = /^[a-z0-9][a-z0-9-]{0,62}[a-z0-9]$/;
const EMAIL_RX = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

function freshForm() {
  return {
    customerName: '',
    customerDisplayName: '',
    billingEmail: '',
    orgName: '',
    tenantName: 'Default Tenant',
    instances: [{ ...DEFAULT_INSTANCE }],
    adminEmail: '',
    adminName: '',
    // RC-78 — Super Admin is the only role with authority over the full
    // customer (per reference_rbac_wildcard_scope). Pre-RC-78 default was
    // Org Admin, which silently locked the first admin out of any second
    // org added later. See ADMIN_ROLE_OPTIONS above for the taxonomy.
    adminRole: 'Super Admin',
    deliveryMode: 'LINK',
    editionKey: 'GOVERNED_WORKFLOW',
    protectionMode: 'OBSERVE_ONLY',
  };
}

export function CustomerOnboardingSlideout({ isOpen, onClose, onCreated }) {
  const { addToast } = useToast();
  /* G588 — Top-level tab. 'single' drives the multi-step wizard; 'bulk'
   * drives the XLSX upload flow. State is kept here (not inside child
   * components) so a parent re-render doesn't lose the operator's
   * in-progress work when they toggle tabs. */
  const [mode, setMode] = useState('single');
  const [step, setStep] = useState(STEP_CUSTOMER);
  const [form, setForm] = useState(freshForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(null);
  /* Smart first-admin field — inline lookup result for form.adminEmail.
   * exists=true means the wizard ATTACHES the existing user as this customer's
   * admin instead of creating a duplicate (no Conflict). */
  const [adminLookup, setAdminLookup] = useState({ checked: false, exists: false, userId: null, name: null, loading: false });
  /* Bulk-mode local state. Kept here so it survives single↔bulk tab
   * flips inside the same slideout open. Cleared when the slideout
   * itself is re-opened (along with everything else). */
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);

  // Debounced inline lookup of the admin email → create-vs-attach decision.
  useEffect(() => {
    const email = (form.adminEmail || '').trim().toLowerCase();
    if (!EMAIL_RX.test(email)) {
      setAdminLookup({ checked: false, exists: false, userId: null, name: null, loading: false });
      return undefined;
    }
    let cancelled = false;
    setAdminLookup((s) => ({ ...s, loading: true }));
    const t = setTimeout(async () => {
      try {
        const res = await api.lookupOnboardingAdmin(email);
        const d = (res && (res.data || res)) || {};
        if (cancelled) return;
        setAdminLookup({ checked: true, exists: !!d.exists, userId: d.userId || null, name: d.name || null, loading: false });
        if (d.exists && d.name) setForm((f) => (f.adminName ? f : { ...f, adminName: d.name }));
      } catch (_) {
        if (!cancelled) setAdminLookup({ checked: true, exists: false, userId: null, name: null, loading: false });
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.adminEmail]);
  /* Refs for focus management on step transitions — the first input of
   * each step gets focus when the step opens, so the user can start
   * typing immediately. */
  const firstInputRef = useRef(null);

  // Reset internal state every open so re-opens don't leak prior form data.
  useEffect(() => {
    if (isOpen) {
      setMode('single');
      setStep(STEP_CUSTOMER);
      setForm(freshForm());
      setSubmitting(false);
      setError(null);
      setResult(null);
      setCopied(null);
      setBulkFile(null);
      setBulkSubmitting(false);
      setBulkError(null);
      setBulkResult(null);
    }
  }, [isOpen]);

  // Focus the first input on every step change (a11y + tactile feel).
  useEffect(() => {
    if (!isOpen) return undefined;
    const t = setTimeout(() => firstInputRef.current?.focus?.(), 30);
    return () => clearTimeout(t);
  }, [step, isOpen]);

  const setField = (key) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: v }));
  };

  const setInstance = (i, key, value) => {
    setForm((f) => {
      const next = f.instances.slice();
      next[i] = { ...next[i], [key]: value };
      // Keep isProduction in sync with tier so the server-side guard sees a coherent payload.
      if (key === 'tier') {
        next[i].isProduction = value === 'PRODUCTION';
      }
      return { ...f, instances: next };
    });
  };

  const addInstance = () => {
    setForm((f) => {
      if (f.instances.length >= 4) return f;
      // Default the second+ instance to non-prod so the operator doesn't
      // accidentally provision two production environments.
      const nextInst = f.instances.length === 0
        ? { ...DEFAULT_INSTANCE }
        : { name: '', tier: 'DEV', isProduction: false, description: '' };
      return { ...f, instances: [...f.instances, nextInst] };
    });
  };

  const removeInstance = (i) => {
    setForm((f) => {
      if (f.instances.length <= 1) return f;
      const next = f.instances.slice();
      next.splice(i, 1);
      return { ...f, instances: next };
    });
  };

  // ── Per-step validation (client-side guards; server is the ultimate truth) ──
  const errors = useMemo(() => {
    const e = {};
    const nameNorm = (form.customerName || '').trim().toLowerCase();
    if (step >= STEP_CUSTOMER) {
      if (!nameNorm) e.customerName = 'Required';
      else if (!CANONICAL_NAME_RX.test(nameNorm))
        e.customerName = 'Use lowercase letters, digits, hyphens (e.g. acme-corp). 2–64 chars.';
      else if (nameNorm === 'legacy') e.customerName = "'legacy' is a reserved name.";
    }
    if (step >= STEP_TOPOLOGY) {
      if (!(form.orgName || '').trim()) e.orgName = 'Required';
      const names = new Set();
      form.instances.forEach((inst, i) => {
        const n = (inst.name || '').trim();
        if (!n) e[`inst-${i}-name`] = 'Required';
        else if (names.has(n.toLowerCase())) e[`inst-${i}-name`] = 'Duplicate name';
        else names.add(n.toLowerCase());
      });
    }
    if (step >= STEP_ADMIN) {
      const em = (form.adminEmail || '').trim();
      if (!em) e.adminEmail = 'Required';
      else if (!EMAIL_RX.test(em)) e.adminEmail = 'Not a valid email address.';
    }
    return e;
  }, [step, form]);

  // Step is "complete" when all errors for its inputs are empty AND every
  // visible required field is non-empty.
  const stepIsValid = useMemo(() => {
    if (step === STEP_CUSTOMER) {
      return !errors.customerName;
    }
    if (step === STEP_TOPOLOGY) {
      if (!form.orgName.trim()) return false;
      if (form.instances.length === 0) return false;
      return !Object.keys(errors).some((k) => k === 'orgName' || k.startsWith('inst-'));
    }
    if (step === STEP_ADMIN) {
      return !errors.adminEmail;
    }
    return true;
  }, [step, form, errors]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    const payload = {
      customerName: form.customerName.trim().toLowerCase(),
      customerDisplayName: form.customerDisplayName.trim() || null,
      billingEmail: form.billingEmail.trim() || null,
      orgName: form.orgName.trim(),
      tenantName: form.tenantName.trim() || 'Default Tenant',
      instances: form.instances.map((i) => ({
        name: i.name.trim(),
        tier: i.tier || 'CUSTOM',
        isProduction: !!i.isProduction,
        description: (i.description || '').trim() || null,
      })),
      adminEmail: form.adminEmail.trim().toLowerCase(),
      adminName: form.adminName.trim() || null,
      // RC-78 — Fallback aligned with the form default; Super Admin is the
      // correct customer-scoped tier for a brand-new customer's first
      // admin. Pre-RC-78 this fell back to "Org Admin" which contradicts
      // the recommendation hint shown on the picker.
      adminRole: form.adminRole || 'Super Admin',
      // Smart admin: attach the existing user (FE inline lookup found a match)
      // instead of creating a duplicate. null → create a new admin.
      existingAdminUserId: adminLookup.exists ? adminLookup.userId : null,
      deliveryMode: form.deliveryMode || 'LINK',
      editionKey: form.editionKey || 'GOVERNED_WORKFLOW',
      protectionMode: form.protectionMode || 'OBSERVE_ONLY',
    };
    try {
      const res = await api.onboardCustomerWithTopology(payload);
      if (!res || res.ok === false) {
        setError(res?.error || 'Onboarding failed');
        setSubmitting(false);
        return;
      }
      const data = res.data || res;
      setResult(data);
      setStep(STEP_RESULT);
      if (typeof onCreated === 'function') onCreated(data);
      addToast({ tone: 'success', message: `${payload.customerName} onboarded` });
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async (text, key) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      addToast({ tone: 'success', message: 'Copied to clipboard' });
      setTimeout(() => setCopied((k) => (k === key ? null : k)), 1500);
    } catch (_) {
      addToast({ tone: 'danger', message: 'Copy failed — select + copy manually' });
    }
  };

  // ── Bulk handlers ────────────────────────────────────────────────
  const handleTemplateDownload = async () => {
    try {
      await api.downloadBulkOnboardingTemplate();
      addToast({ tone: 'success', message: 'Template downloaded' });
    } catch (e) {
      addToast({ tone: 'danger', message: e?.message || 'Template download failed' });
    }
  };

  const handleBulkSubmit = async () => {
    if (!bulkFile) {
      setBulkError('Choose an XLSX file first');
      return;
    }
    setBulkSubmitting(true);
    setBulkError(null);
    setBulkResult(null);
    try {
      const res = await api.bulkOnboardCustomers(bulkFile);
      if (!res || res.ok === false) {
        setBulkError(res?.error || 'Bulk onboarding failed');
        return;
      }
      const data = res.data || res;
      setBulkResult(data);
      if (typeof onCreated === 'function' && data?.succeeded > 0) onCreated(data);
      addToast({
        tone: data.failed > 0 ? 'warning' : 'success',
        message: data.failed > 0
          ? `${data.succeeded} onboarded · ${data.failed} failed`
          : `${data.succeeded} customers onboarded`,
      });
    } catch (e) {
      setBulkError(e?.message || String(e));
    } finally {
      setBulkSubmitting(false);
    }
  };

  // ── Header (mode-aware) ────────────────────────────────────────────
  const header = (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Building2 size={14} className="text-v2-action-strong" aria-hidden="true" />
        <Pill tone={step === STEP_RESULT && result ? 'success' : 'muted'} size="sm" dot={step === STEP_RESULT && !!result}>
          {step === STEP_RESULT && result ? 'Onboarded' : 'Onboard'}
        </Pill>
      </div>
      <h2 className="text-[16px] font-bold tracking-tight m-0">
        {step === STEP_RESULT && result ? 'Customer onboarded' : 'Onboard customer'}
      </h2>
      <p className="text-[12px] text-v2-ink-secondary mt-1 mb-0">
        {step === STEP_RESULT && result
          ? <>The full topology has been provisioned. Share the {result.deliveryMode === 'LINK' ? 'reset link' : 'temporary password'} with the admin via a secure channel.</>
          : 'Provision a brand-new customer with starter org, tenant, instances, and first admin.'}
      </p>
      {/* Step indicator — hidden in bulk mode + on result page. */}
      {mode === 'single' && step !== STEP_RESULT && (
        <div className="flex items-center gap-2 mt-3" role="progressbar"
             aria-valuemin={1} aria-valuemax={STEP_LABELS.length} aria-valuenow={step + 1}>
          {STEP_LABELS.map((label, i) => {
            const state = i < step ? 'done' : i === step ? 'current' : 'pending';
            return (
              <React.Fragment key={label}>
                <div className="flex items-center gap-1.5">
                  <span className={
                    'inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold '
                    + (state === 'done'
                      ? 'bg-v2-success text-white'
                      : state === 'current'
                      ? 'bg-v2-action text-v2-action-on'
                      : 'bg-v2-muted text-v2-ink-muted')
                  }>
                    {state === 'done' ? <Check size={11} /> : i + 1}
                  </span>
                  <span className={
                    'text-[11px] font-medium '
                    + (state === 'current' ? 'text-v2-ink' : 'text-v2-ink-muted')
                  }>{label}</span>
                </div>
                {i < STEP_LABELS.length - 1 && (
                  <span aria-hidden="true" className="flex-1 h-px bg-v2-border-subtle min-w-[12px] max-w-[24px]" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );

  // ── Footer (mode + step aware) ─────────────────────────────────────
  const footer = (() => {
    if (mode === 'bulk') {
      if (bulkResult) {
        return <Button variant="primary" size="sm" onClick={onClose}>Done</Button>;
      }
      return (
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={bulkSubmitting}>Cancel</Button>
          <Button variant="primary" size="sm"
            onClick={handleBulkSubmit}
            disabled={!bulkFile || bulkSubmitting}>
            {bulkSubmitting ? 'Onboarding…' : 'Onboard all'}
          </Button>
        </>
      );
    }
    if (step === STEP_RESULT) {
      return <Button variant="primary" size="sm" onClick={onClose}>Done</Button>;
    }
    if (step === STEP_REVIEW) {
      return (
        <>
          <Button variant="ghost" size="sm" iconLeft={<ArrowLeft size={13} />}
            onClick={() => setStep(STEP_ADMIN)} disabled={submitting}>Back</Button>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" size="sm"
            onClick={submit} disabled={submitting || !stepIsValid}>
            {submitting ? 'Onboarding…' : 'Onboard customer'}
          </Button>
        </>
      );
    }
    return (
      <>
        {step > STEP_CUSTOMER && (
          <Button variant="ghost" size="sm" iconLeft={<ArrowLeft size={13} />}
            onClick={() => setStep((s) => s - 1)}>Back</Button>
        )}
        <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
        <Button variant="primary" size="sm" iconRight={<ArrowRight size={13} />}
          onClick={() => setStep((s) => s + 1)} disabled={!stepIsValid}>
          Next
        </Button>
      </>
    );
  })();

  /* G588 — Hide the tab strip once the wizard has produced a result.
   * Letting an operator flip back to Bulk after they just minted a
   * one-shot temp password would silently lose the credential — the
   * result-state slideout body is the only place that cleartext lives. */
  const tabs = (step === STEP_RESULT || bulkResult) ? undefined : [
    { id: 'single', label: 'Single' },
    { id: 'bulk',   label: 'Bulk upload' },
  ];

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel="Onboard customer"
      header={header}
      footer={footer}
      width={640}
      tabs={tabs}
      activeTabId={mode}
      onTabChange={(id) => setMode(id === 'bulk' ? 'bulk' : 'single')}
      dismissOnBackdrop={!submitting && !bulkSubmitting}
    >
      {/* ── BULK MODE ─────────────────────────────────────────────── */}
      {mode === 'bulk' && (
        <BulkView
          file={bulkFile}
          onPickFile={setBulkFile}
          submitting={bulkSubmitting}
          error={bulkError}
          result={bulkResult}
          onDownloadTemplate={handleTemplateDownload}
          onCopy={handleCopy}
          copied={copied}
        />
      )}

      {/* ── Step 1: Customer ─────────────────────────────────────── */}
      {mode === 'single' && step === STEP_CUSTOMER && (
        <FieldGrid>
          <Field label="Canonical name" hint="Lowercase, no spaces. Used in audit + integrations. Immutable post-create."
                 required error={errors.customerName}>
            <input ref={firstInputRef} type="text" value={form.customerName}
              onChange={setField('customerName')} placeholder="acme-corp" className={inputCls(!!errors.customerName)} />
          </Field>
          <Field label="Display name" hint="Friendly label shown in pickers + headers.">
            <input type="text" value={form.customerDisplayName}
              onChange={setField('customerDisplayName')} placeholder="Acme Corporation" className={inputCls()} />
          </Field>
          <Field label="Billing email" hint="Optional. Captured on the audit row for support routing.">
            <input type="email" value={form.billingEmail}
              onChange={setField('billingEmail')} placeholder="billing@acme.com" className={inputCls()} />
          </Field>
          <Field label="Edition (plan)" hint="Seeds the customer's module entitlements. Prerequisites are added automatically.">
            <select value={form.editionKey} onChange={setField('editionKey')} className={inputCls()}>
              <option value="GOVERNED_WORKFLOW">Protected Workflow — orchestration, workflow protection, protection layer</option>
              <option value="AGENTIC_PACK">Agentic Pack — adds agents, trust &amp; identity for App Packs</option>
              <option value="AGENTIC_GOVERNANCE">Agentic Protection — adds agents, identity, gateway</option>
              <option value="CONTINUOUS_TRUST">Continuous Trust — full platform</option>
            </select>
          </Field>
          <Field label="Workflow Protection" hint="Observe-only (shadow) is the recommended trial posture — evaluates but never blocks.">
            <select value={form.protectionMode} onChange={setField('protectionMode')} className={inputCls()}>
              <option value="OBSERVE_ONLY">Observe-only (shadow)</option>
              <option value="ENTITLED">Enforce (block on violation)</option>
            </select>
          </Field>
        </FieldGrid>
      )}

      {/* ── Step 2: Topology ─────────────────────────────────────── */}
      {mode === 'single' && step === STEP_TOPOLOGY && (
        <FieldGrid>
          <Field label="Organization name" hint="The first Organization under this customer." required error={errors.orgName}>
            <input ref={firstInputRef} type="text" value={form.orgName}
              onChange={setField('orgName')} placeholder="Acme Engineering" className={inputCls(!!errors.orgName)} />
          </Field>
          <Field label="Tenant name" hint="The first Tenant under the org. Defaults to “Default Tenant”.">
            <input type="text" value={form.tenantName}
              onChange={setField('tenantName')} placeholder="Default Tenant" className={inputCls()} />
          </Field>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div>
                <div className="text-[12px] font-medium text-v2-ink">Instances <span className="text-v2-danger">*</span></div>
                <div className="text-[11px] text-v2-ink-muted">At least one. Maximum four per onboard.</div>
              </div>
              <Button variant="ghost" size="sm" iconLeft={<Plus size={13} />}
                onClick={addInstance} disabled={form.instances.length >= 4}>
                Add instance
              </Button>
            </div>
            <div className="space-y-2">
              {form.instances.map((inst, i) => (
                <Card key={i} padding="sm">
                  <Card.Body>
                    <div className="flex items-start gap-2 mb-2">
                      <Boxes size={14} className="text-v2-ink-muted flex-shrink-0 mt-0.5" />
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <Field label="Name" required error={errors[`inst-${i}-name`]}>
                          <input type="text" value={inst.name}
                            onChange={(e) => setInstance(i, 'name', e.target.value)}
                            placeholder={i === 0 ? 'Production' : 'Staging'}
                            className={inputCls(!!errors[`inst-${i}-name`])} />
                        </Field>
                        <Field label="Tier">
                          <select value={inst.tier} onChange={(e) => setInstance(i, 'tier', e.target.value)}
                            className={inputCls()}>
                            {TIER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </Field>
                      </div>
                      {form.instances.length > 1 && (
                        <Button variant="ghost" size="sm" className="!text-v2-danger"
                          onClick={() => removeInstance(i)} title="Remove instance">
                          <Trash2 size={13} />
                        </Button>
                      )}
                    </div>
                    <Field label="Description" hint="Optional.">
                      <input type="text" value={inst.description}
                        onChange={(e) => setInstance(i, 'description', e.target.value)}
                        placeholder="e.g. production environment, customer-facing" className={inputCls()} />
                    </Field>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </div>
        </FieldGrid>
      )}

      {/* ── Step 3: Admin ────────────────────────────────────────── */}
      {mode === 'single' && step === STEP_ADMIN && (
        <FieldGrid>
          <Field label="Admin email" hint="The first Customer Admin's login. Must be unique across the platform." required error={errors.adminEmail}>
            <input ref={firstInputRef} type="email" value={form.adminEmail}
              onChange={setField('adminEmail')} placeholder="admin@new-customer.com" className={inputCls(!!errors.adminEmail)} />
            {adminLookup.loading && (
              <div className="text-[11px] text-v2-ink-muted mt-1">Checking…</div>
            )}
            {!adminLookup.loading && adminLookup.checked && adminLookup.exists && (
              <div className="text-[11px] text-v2-action mt-1">
                Existing user{adminLookup.name ? ` · ${adminLookup.name}` : ''} — will be attached as {form.adminRole} on this customer (keeps their current login).
              </div>
            )}
            {!adminLookup.loading && adminLookup.checked && !adminLookup.exists && (
              <div className="text-[11px] text-v2-success mt-1">New user — will be created as {form.adminRole}.</div>
            )}
          </Field>
          <Field label="Admin name" hint="Display name shown to other operators. Defaults to the email's local-part.">
            <input type="text" value={form.adminName}
              onChange={setField('adminName')} placeholder="Alice Chen" className={inputCls()} />
          </Field>
          {/* RC-78 — Hint now matches the option taxonomy above. Super
              Admin is the only role with full-customer authority; Org
              Admin and Tenant Admin are narrower. */}
          <Field label="Admin role" hint="Recommended: Super Admin (full authority across this customer — every org, every tenant, every instance).">
            <select value={form.adminRole} onChange={setField('adminRole')} className={inputCls()}>
              {ADMIN_ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label} — {o.hint}</option>
              ))}
            </select>
          </Field>
          {!adminLookup.exists && (
          <div>
            <div className="text-[12px] font-medium text-v2-ink mb-1">Credential delivery <span className="text-v2-danger">*</span></div>
            <div className="text-[11px] text-v2-ink-muted mb-2">How to get the admin's first sign-in credentials.</div>
            <div className="space-y-2">
              <RadioCard
                checked={form.deliveryMode === 'LINK'}
                onChange={() => setField('deliveryMode')('LINK')}
                title="Send a reset link"
                hint="Backend mints a one-time token (24h). You'll get the URL to share — the admin sets their own password. Recommended."
                name="onboard-delivery"
              />
              <RadioCard
                checked={form.deliveryMode === 'TEMP_PASSWORD'}
                onChange={() => setField('deliveryMode')('TEMP_PASSWORD')}
                title="Generate a temporary password"
                hint="Strong random password shown to you exactly once. Use when email isn't available."
                name="onboard-delivery"
              />
            </div>
          </div>
          )}
        </FieldGrid>
      )}

      {/* ── Step 4: Review ───────────────────────────────────────── */}
      {mode === 'single' && step === STEP_REVIEW && (
        <div className="space-y-4">
          <SummaryCard icon={<Building2 size={14} />} title="Customer" rows={[
            ['Canonical name', form.customerName],
            ['Display name',   form.customerDisplayName || `(${form.customerName})`],
            ['Billing email',  form.billingEmail || '—'],
            ['Edition',        form.editionKey],
            ['Protection',     form.protectionMode === 'ENTITLED' ? 'Enforce' : 'Observe-only'],
          ]} />
          <SummaryCard icon={<Server size={14} />} title="Topology" rows={[
            ['Organization', form.orgName],
            ['Tenant',       form.tenantName || 'Default Tenant'],
            ['Instances',    (
              <div className="flex flex-wrap gap-1.5">
                {form.instances.map((i, idx) => (
                  <Pill key={idx} tone={i.isProduction ? 'danger' : 'action'} size="sm" dot>
                    {i.name} · {i.tier}
                  </Pill>
                ))}
              </div>
            )],
          ]} />
          <SummaryCard icon={<UserPlus size={14} />} title="First admin" rows={[
            ['Email', form.adminEmail],
            ['Name',  form.adminName || `(${form.adminEmail.split('@')[0] || 'admin'})`],
            ['Role',  form.adminRole],
            ['Credential delivery', adminLookup.exists ? 'Existing user — attached (keeps current login)' : (form.deliveryMode === 'LINK' ? 'One-time reset link (24h)' : 'Temporary password (shown once)')],
          ]} />
          {error && (
            <div className="p-3 rounded-v2-sm bg-v2-danger-soft border border-v2-danger/30 text-[12.5px] text-v2-danger flex items-start gap-2">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold mb-0.5">Onboarding failed</div>
                <div className="text-v2-ink">{error}</div>
                <div className="text-[11px] text-v2-ink-muted mt-1">
                  No partial state was created — the database has been rolled back. Step back to correct the field and try again.
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Step 5: Result ───────────────────────────────────────── */}
      {mode === 'single' && step === STEP_RESULT && result && (
        <div className="space-y-4">
          <SummaryCard icon={<Check size={14} className="text-v2-success" />} title="Provisioned" rows={[
            ['Customer',      `${result.customerName} (${shortId(result.customerId)})`],
            ['Org / Tenant',  `${shortId(result.orgId)} / ${shortId(result.tenantId)}`],
            ['Instances',     (result.instanceIds || []).length],
            ['Admin',         `${result.adminEmail} (${shortId(result.adminUserId)})`],
          ]} />

          {result.deliveryMode === 'EXISTING' && (
            <div className="text-[12px] text-v2-ink-muted">
              An existing user was attached as this customer's admin — they sign in with their current credentials. No new credential was issued.
            </div>
          )}

          {result.deliveryMode === 'LINK' && result.resetUrl && (
            <div>
              <label className="block text-[12px] font-medium text-v2-ink mb-1">Reset link</label>
              <div className="flex items-stretch gap-2">
                <input type="text" readOnly value={result.resetUrl}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 h-9 px-2.5 rounded-v2-sm border border-v2-border-default bg-v2-muted text-[12px] font-mono-v2 text-v2-ink" />
                <Button variant="secondary" size="sm"
                  iconLeft={copied === 'url' ? <Check size={13} /> : <Copy size={13} />}
                  onClick={() => handleCopy(result.resetUrl, 'url')}>Copy</Button>
                <Button variant="ghost" size="sm"
                  iconLeft={<ExternalLink size={13} />}
                  onClick={() => window.open(result.resetUrl, '_blank', 'noopener,noreferrer')}
                  title="Open in a new tab">Open</Button>
              </div>
              {result.resetExpiresAt && (
                <div className="text-[11.5px] text-v2-ink-muted mt-1.5">
                  Expires {new Date(result.resetExpiresAt).toLocaleString()}. One-time use.
                </div>
              )}
              <div className="mt-3 p-3 rounded-v2-sm bg-v2-action-soft border border-v2-action/30 text-[12px] text-v2-ink flex items-start gap-2">
                <Mail size={14} className="text-v2-action flex-shrink-0 mt-0.5" />
                <span>Share this link with {result.adminEmail} via your usual secure channel. When they click it they'll be prompted to set a new password.</span>
              </div>
            </div>
          )}

          {result.deliveryMode === 'TEMP_PASSWORD' && result.adminTempPassword && (
            <div>
              <label className="block text-[12px] font-medium text-v2-ink mb-1">Temporary password</label>
              <div className="flex items-stretch gap-2">
                <input type="text" readOnly value={result.adminTempPassword}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 h-9 px-2.5 rounded-v2-sm border border-v2-border-default bg-v2-muted text-[13px] font-mono-v2 tracking-wider text-v2-ink" />
                <Button variant="secondary" size="sm"
                  iconLeft={copied === 'pw' ? <Check size={13} /> : <Copy size={13} />}
                  onClick={() => handleCopy(result.adminTempPassword, 'pw')}>Copy</Button>
              </div>
              <div className="mt-3 p-3 rounded-v2-sm bg-v2-danger-soft border border-v2-danger/30 text-[12px] text-v2-ink flex items-start gap-2">
                <ShieldAlert size={14} className="text-v2-danger flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-v2-danger mb-0.5">Shown only once</div>
                  This password is not stored anywhere after you close this dialog. Copy it now and share it with {result.adminEmail} via a secure channel. They should change it on first login.
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Slideout>
  );
}

// ── Small presentation helpers ────────────────────────────────────────

function FieldGrid({ children }) {
  return <div className="space-y-4">{children}</div>;
}

function Field({ label, hint, required, error, children }) {
  return (
    <div>
      <label className="block text-[12px] font-medium text-v2-ink mb-1">
        {label}{required && <span className="text-v2-danger"> *</span>}
      </label>
      {children}
      {hint && !error && <div className="text-[11px] text-v2-ink-muted mt-1">{hint}</div>}
      {error && <div className="text-[11.5px] text-v2-danger mt-1 flex items-center gap-1">
        <AlertTriangle size={11} aria-hidden="true" /> {error}
      </div>}
    </div>
  );
}

function inputCls(invalid) {
  return (
    'w-full h-9 px-2.5 rounded-v2-sm border bg-v2-surface text-[13px] text-v2-ink '
    + 'focus:outline-none focus:border-v2-action '
    + (invalid ? 'border-v2-danger' : 'border-v2-border-default')
  );
}

function RadioCard({ checked, onChange, title, hint, name }) {
  return (
    <label className={
      'flex items-start gap-3 p-3 rounded-v2-sm border cursor-pointer transition-colors '
      + (checked
          ? 'border-v2-action bg-v2-action-soft'
          : 'border-v2-border-default hover:bg-v2-muted')
    }>
      <input type="radio" className="mt-0.5" checked={checked} onChange={onChange} name={name} />
      <div className="flex-1">
        <div className="text-[13px] font-semibold text-v2-ink">{title}</div>
        <div className="text-[11.5px] text-v2-ink-muted mt-0.5">{hint}</div>
      </div>
    </label>
  );
}

function SummaryCard({ icon, title, rows }) {
  return (
    <Card padding="sm">
      <Card.Header title={<span className="flex items-center gap-1.5 text-v2-ink-muted">{icon}<span className="text-v2-ink font-semibold">{title}</span></span>} />
      <Card.Body>
        <dl className="grid grid-cols-[140px_1fr] gap-y-1.5 gap-x-3 text-[12.5px]">
          {rows.map(([k, v], i) => (
            <React.Fragment key={i}>
              <dt className="text-v2-ink-muted text-[11px] uppercase tracking-wider font-semibold">{k}</dt>
              <dd className="text-v2-ink">{v}</dd>
            </React.Fragment>
          ))}
        </dl>
      </Card.Body>
    </Card>
  );
}

function shortId(id) {
  if (!id) return '—';
  const s = String(id);
  return s.length > 10 ? s.slice(0, 8) + '…' : s;
}

/**
 * G588 — Bulk-upload view rendered inside the slideout body when
 * mode === 'bulk'. Three phases driven by the parent's state:
 *   1. Idle      — show template-download CTA + file picker
 *   2. File ready — show the chosen file + summary; footer enables Submit
 *   3. Result    — show per-row outcomes (succeeded vs failed) with the
 *                  one-shot credentials for every successful row
 *
 * Stateless wrt server progress: the bulk endpoint returns when ALL rows
 * have completed (sub-second per row), so we don't need polling. If a
 * future enhancement adds async bulk runs, this view can be upgraded to
 * a streaming progress table without touching the parent.
 */
function BulkView({ file, onPickFile, submitting, error, result, onDownloadTemplate, onCopy, copied }) {
  if (result) {
    return (
      <div className="space-y-4">
        <Card padding="sm">
          <Card.Header title={
            <span className="flex items-center gap-1.5 text-v2-ink-muted">
              <FileSpreadsheet size={14} />
              <span className="text-v2-ink font-semibold">Bulk run summary</span>
            </span>
          } />
          <Card.Body>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted">Total</div>
                <div className="text-[20px] font-bold text-v2-ink tabular-nums">{result.totalRows}</div>
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted">Succeeded</div>
                <div className="text-[20px] font-bold text-v2-success tabular-nums">{result.succeeded}</div>
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted">Failed</div>
                <div className={'text-[20px] font-bold tabular-nums ' + (result.failed > 0 ? 'text-v2-danger' : 'text-v2-ink-muted')}>
                  {result.failed}
                </div>
              </div>
            </div>
            <div className="text-[11px] text-v2-ink-muted text-center mt-2">
              Completed in {(result.durationMs / 1000).toFixed(1)}s
            </div>
          </Card.Body>
        </Card>

        <div className="space-y-2">
          {(result.outcomes || []).map((o, i) => (
            <Card key={i} padding="sm">
              <Card.Body>
                <div className="flex items-start gap-2">
                  <span className="flex-shrink-0 mt-0.5">
                    {o.status === 'SUCCEEDED'
                      ? <Check size={14} className="text-v2-success" />
                      : <AlertTriangle size={14} className="text-v2-danger" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[13px] font-semibold text-v2-ink">{o.customerName}</span>
                      <Pill tone={o.status === 'SUCCEEDED' ? 'success' : 'danger'} size="sm" dot>
                        {o.status === 'SUCCEEDED' ? 'Onboarded' : 'Failed'}
                      </Pill>
                    </div>
                    {o.status === 'FAILED' && o.errorMessage && (
                      <div className="text-[11.5px] text-v2-danger mt-1">{o.errorMessage}</div>
                    )}
                    {o.status === 'SUCCEEDED' && (
                      <div className="text-[11.5px] text-v2-ink-muted mt-1">
                        {o.adminEmail} · {o.deliveryMode === 'LINK' ? 'reset link' : 'temp password'} issued
                      </div>
                    )}
                    {o.status === 'SUCCEEDED' && o.deliveryMode === 'LINK' && o.resetUrl && (
                      <div className="flex items-stretch gap-2 mt-2">
                        <input type="text" readOnly value={o.resetUrl}
                          onFocus={(e) => e.target.select()}
                          className="flex-1 h-7 px-2 rounded-v2-sm border border-v2-border-default bg-v2-muted text-[11px] font-mono-v2 text-v2-ink" />
                        <Button variant="ghost" size="sm"
                          iconLeft={copied === `url-${i}` ? <Check size={12} /> : <Copy size={12} />}
                          onClick={() => onCopy(o.resetUrl, `url-${i}`)}>Copy</Button>
                      </div>
                    )}
                    {o.status === 'SUCCEEDED' && o.deliveryMode === 'TEMP_PASSWORD' && o.adminTempPassword && (
                      <div className="flex items-stretch gap-2 mt-2">
                        <input type="text" readOnly value={o.adminTempPassword}
                          onFocus={(e) => e.target.select()}
                          className="flex-1 h-7 px-2 rounded-v2-sm border border-v2-border-default bg-v2-muted text-[12px] font-mono-v2 tracking-wider text-v2-ink" />
                        <Button variant="ghost" size="sm"
                          iconLeft={copied === `pw-${i}` ? <Check size={12} /> : <Copy size={12} />}
                          onClick={() => onCopy(o.adminTempPassword, `pw-${i}`)}>Copy</Button>
                      </div>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>

        {result.failed > 0 && (
          <div className="p-3 rounded-v2-sm bg-v2-warning-soft border border-v2-warning/30 text-[12px] text-v2-ink flex items-start gap-2">
            <AlertTriangle size={14} className="text-v2-warning flex-shrink-0 mt-0.5" />
            <span>
              {result.failed} customer{result.failed === 1 ? '' : 's'} failed. Fix the offending rows in your XLSX and re-upload — successful rows are already committed and don't need to be re-included.
            </span>
          </div>
        )}

        {result.succeeded > 0 && (
          <div className="p-3 rounded-v2-sm bg-v2-danger-soft border border-v2-danger/30 text-[12px] text-v2-ink flex items-start gap-2">
            <ShieldAlert size={14} className="text-v2-danger flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-v2-danger mb-0.5">One-shot credentials above</div>
              Reset links and temporary passwords are shown exactly once. Copy and distribute them now — closing this slideout drops them permanently.
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card padding="sm">
        <Card.Body>
          <div className="flex items-start gap-3">
            <FileSpreadsheet size={18} className="text-v2-action-strong flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-v2-ink">Bulk onboard from XLSX</div>
              <div className="text-[12px] text-v2-ink-secondary mt-1 leading-relaxed">
                Upload a workbook with sheets <span className="font-mono-v2">Customers</span>,{' '}
                <span className="font-mono-v2">Orgs</span>,{' '}
                <span className="font-mono-v2">Tenants</span>,{' '}
                <span className="font-mono-v2">Instances</span>,{' '}
                <span className="font-mono-v2">Admins</span>. Each customer row is onboarded
                in its own transaction; failures don't block other rows.
              </div>
              <div className="mt-3">
                <Button variant="secondary" size="sm" iconLeft={<Download size={13} />}
                  onClick={onDownloadTemplate}>
                  Download empty template
                </Button>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <div>
        <label className="block text-[12px] font-medium text-v2-ink mb-1">
          Workbook <span className="text-v2-danger">*</span>
        </label>
        <label className={
          'flex items-center gap-3 p-3 rounded-v2-sm border-2 border-dashed cursor-pointer transition-colors '
          + (file
              ? 'border-v2-action bg-v2-action-soft'
              : 'border-v2-border-default hover:bg-v2-muted')
        }>
          <Upload size={18} className={file ? 'text-v2-action' : 'text-v2-ink-muted'} />
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-medium text-v2-ink truncate">
              {file ? file.name : 'Drop XLSX here or click to choose'}
            </div>
            <div className="text-[11px] text-v2-ink-muted mt-0.5">
              {file
                ? `${(file.size / 1024).toFixed(1)} KB · ready to onboard`
                : '.xlsx only · authored from the template above'}
            </div>
          </div>
          <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(e) => onPickFile(e.target.files && e.target.files[0])} />
        </label>
      </div>

      {error && (
        <div className="p-3 rounded-v2-sm bg-v2-danger-soft border border-v2-danger/30 text-[12.5px] text-v2-danger flex items-start gap-2">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold mb-0.5">Upload failed</div>
            <div className="text-v2-ink">{error}</div>
          </div>
        </div>
      )}

      {submitting && (
        <div className="p-3 rounded-v2-sm bg-v2-action-soft border border-v2-action/30 text-[12.5px] text-v2-ink flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full border-2 border-v2-action border-t-transparent animate-spin" />
          Parsing workbook + onboarding each customer in turn…
        </div>
      )}
    </div>
  );
}

export default CustomerOnboardingSlideout;
